import { Link } from "react-router-dom";
import {
  Checklist,
  Glossary,
  GuideActions,
  GuideCallout,
  GuideSection,
  GuideToc,
  QuickReference,
  StepList,
} from "../../components/guide/GuideDocument";
import { PageHeader } from "../../components/PageHeader";

const guideDate = "June 5, 2026";

const toc = [
  { id: "overview", title: "Admin Overview" },
  { id: "admin-login", title: "Admin Login" },
  { id: "creating-client", title: "Creating a Client" },
  { id: "creating-vault", title: "Creating an Event Vault" },
  { id: "slug-rules", title: "Slug Rules" },
  { id: "guest-links", title: "Generating Guest Upload Links" },
  { id: "guest-testing", title: "Guest Upload Testing" },
  { id: "reviewing-media", title: "Reviewing Media" },
  { id: "approval-workflow", title: "Approval Workflow" },
  { id: "hidden-workflow", title: "Hidden / Private Media" },
  { id: "not-approved-workflow", title: "Not Approved Media" },
  { id: "featured-workflow", title: "Featured Media" },
  { id: "public-gallery", title: "Public Gallery" },
  { id: "client-dashboard", title: "Client Dashboard" },
  { id: "multiple-vaults", title: "Multiple Vaults Per Client" },
  { id: "practice-workflow", title: "Practice Workflow" },
  { id: "production-safety", title: "Production Safety Rules" },
  { id: "storage-notes", title: "Storage & Billing Notes" },
  { id: "troubleshooting", title: "Troubleshooting" },
  { id: "quick-reference", title: "Quick Reference Checklist" },
  { id: "glossary", title: "Glossary of Terms" },
];

const loginSteps = [
  "Go to the admin login route.",
  "Enter the approved Kreatethedate admin email address.",
  "Open the Brevo email containing the one-time login code.",
  "Enter the code in the login screen.",
  "Open the admin dashboard or the Memory Media Vault admin area.",
];

const clientSteps = [
  "Open Clients in the admin sidebar.",
  "Enter the client name and client email.",
  "Create a clean client slug using lowercase letters and hyphens.",
  "Create or confirm the client login user with login name, login email, and role.",
  "Use the same email when the client profile email is also the portal login email.",
  "Avoid duplicate client records by searching existing clients before creating a new one.",
];

const vaultSteps = [
  "Open Events in the admin sidebar.",
  "Select the correct client before entering event details.",
  "Add the event/vault name, event type, event date, event slug, and vault display name.",
  "Add optional primary color, secondary color, accent color, cover/banner image, and optional logo/photo URL when available.",
  "Add a welcome message and guest upload instructions that match the client's event.",
  "Save the event vault and confirm it appears under the selected client.",
];

const uploadLinkSteps = [
  "Open Events and find the correct client event vault.",
  "Generate the guest upload link from the event vault card.",
  "Copy the link and open it in a new tab before sharing.",
  "Regenerate the link only when the existing link should be replaced.",
  "Do not send real links to guests until the vault details and upload page are ready.",
];

const testSteps = [
  "Open the guest upload link.",
  "Submit one small image with a clear test uploader name.",
  "Confirm the success message appears.",
  "Go to Admin Media.",
  "Confirm the upload appears as Pending Review.",
];

const approveSteps = [
  "Go to Admin Media.",
  "Find the pending upload.",
  "Review uploader details, caption, file type, and preview.",
  "Approve the upload.",
  "Confirm the label changes to Approved.",
  "Open the public gallery and confirm approved media appears.",
];

const practiceChecklist = [
  "Log in as admin",
  "Create practice client",
  "Create event vault",
  "Generate guest upload link",
  "Open upload link",
  "Upload one small test image",
  "Confirm pending review",
  "Approve upload",
  "Confirm gallery visibility",
  "Log in as client",
  "Confirm client dashboard",
  "Confirm no other client data appears",
  "Test hide/feature/not approved if needed",
];

const safetyChecklist = [
  "Do not send guest link until event details are correct.",
  "Confirm event date displays correctly.",
  "Confirm client name is correct.",
  "Confirm slug is correct.",
  "Confirm gallery works.",
  "Confirm client login works.",
  "Do not delete production data without intention.",
];

const adminQuickReference = [
  { label: "Admin login route", detail: "Use the admin login page with the approved admin email and Brevo one-time code." },
  { label: "Clients", detail: "Create client records and client login access." },
  { label: "Events", detail: "Create event vaults, customize vault settings, and generate guest upload links." },
  { label: "Memory Media Vault", detail: "View the broader admin vault workspace." },
  { label: "Media", detail: "Review uploads across clients and manage statuses." },
  { label: "Storage Setup", detail: "Review Cloudflare setup, ownership, billing, and upload-limit reminders." },
  { label: "Activity Log", detail: "Check recent setup, upload, and approval activity." },
  { label: "Settings", detail: "Review read-only environment/support status and open this manual." },
  { label: "User Manual", detail: "Return to this operating guide when onboarding or troubleshooting." },
];

const adminAreas = [
  "clients",
  "events/vaults",
  "upload links",
  "media",
  "pending review",
  "approved media",
  "hidden/private media",
  "not approved media",
  "featured media",
  "storage setup",
  "activity log",
  "settings",
];

const glossary = [
  { term: "Memory Media Vault", definition: "The reusable platform for collecting, reviewing, approving, and organizing event media." },
  { term: "Event Vault", definition: "A client-specific event workspace with its own upload link and gallery." },
  { term: "Guest Upload Link", definition: "The private event link guests use to upload photos and videos." },
  { term: "Pending Review", definition: "Uploaded media waiting for approval or another review action." },
  { term: "Approved", definition: "Media allowed to appear in the public gallery." },
  { term: "Featured", definition: "Approved media highlighted as a favorite in the curated vault view." },
  { term: "Hidden / Private", definition: "Media stored privately and excluded from public display." },
  { term: "Not Approved", definition: "Media rejected from public display." },
  { term: "Public Gallery", definition: "The public-facing gallery for approved media." },
  { term: "Client Dashboard", definition: "The client portal home area scoped to that client's assigned vaults and media." },
  { term: "Storage", definition: "Cloud space used by uploaded photos and videos." },
  { term: "Slug", definition: "A URL-friendly identifier, usually lowercase words separated by hyphens." },
  { term: "Client Login", definition: "The one-time-code login a client uses to access their portal." },
  { term: "Admin Portal", definition: "The Kreatethedate area for setup, support, media review, and system oversight." },
];

const troubleshooting = [
  { issue: "Client cannot log in.", fix: "Confirm the client user exists, the email is correct, and the user is assigned to the correct client." },
  { issue: "Login code not received.", fix: "Confirm the email address, check spam, and confirm Brevo delivery is configured for the environment." },
  { issue: "Guest upload link shows an error.", fix: "Confirm the link belongs to an active guest upload record and the event vault is set up." },
  { issue: "Upload not appearing in pending review.", fix: "Confirm the guest saw the success message and that Admin Media is filtered to the correct client or all clients." },
  { issue: "Approved media not showing in gallery.", fix: "Confirm status is Approved and the gallery URL is for the correct client and event slug." },
  { issue: "Date displays incorrectly.", fix: "Confirm the date was entered correctly as a date-only value and retest the vault card, upload page, and gallery." },
  { issue: "Wrong client selected.", fix: "Return to Events and confirm the selected client before creating or editing a vault." },
  { issue: "Duplicate event vault created.", fix: "Use the existing correct vault going forward, and hide or ignore test media as needed before cleanup." },
  { issue: "Need to regenerate guest link.", fix: "Open the event vault card and regenerate only when the previous link should stop being used." },
  { issue: "Need to hide test media.", fix: "Open Admin Media and use Hide, Not Approved, or Delete only when intentional." },
];

export function AdminUserManualPage() {
  return (
    <div className="guide-document grid gap-8">
      <PageHeader
        eyebrow="Admin Operating Manual"
        title="Memory Media Vault Admin Manual"
        subtitle={`Version 1.0 | Last updated: ${guideDate}`}
      >
        <GuideActions />
      </PageHeader>

      <GuideToc items={toc} />

      <GuideSection id="overview" title="Admin Overview">
        <p>
          The admin area manages clients, event vaults, guest upload links, uploads, approvals, galleries, storage reminders,
          activity, and settings.
        </p>
        <p>
          Admins can support multiple clients and multiple event vaults inside one reusable Memory Media Vault platform.
          Do not rebuild the app for each client.
        </p>
      </GuideSection>

      <GuideSection id="admin-login" title="Admin Login">
        <StepList items={loginSteps} />
      </GuideSection>

      <GuideSection id="creating-client" title="Creating a Client">
        <StepList items={clientSteps} />
        <GuideCallout title="Best practice">
          <p>Search existing clients before creating a new one. Duplicate client records can make setup harder to support.</p>
        </GuideCallout>
      </GuideSection>

      <GuideSection id="creating-vault" title="Creating an Event Vault">
        <StepList items={vaultSteps} />
        <GuideCallout title="Personalization fields">
          <p>
            Preserve the cover/banner image, accent color, primary color, secondary color, welcome message, guest upload
            instructions, optional logo/photo URL, and vault display name for each client/event.
          </p>
        </GuideCallout>
      </GuideSection>

      <GuideSection id="slug-rules" title="Slug Rules">
        <Checklist
          items={[
            "Use lowercase letters.",
            "Use hyphens instead of spaces.",
            "Do not use apostrophes.",
            "Do not use special characters.",
            "Make slugs specific to the client and event.",
          ]}
        />
        <GuideCallout title="Examples">
          <p>latonya-44th-birthday</p>
          <p>client-name-wedding</p>
          <p>smith-family-reunion-2026</p>
        </GuideCallout>
      </GuideSection>

      <GuideSection id="guest-links" title="Generating Guest Upload Links">
        <StepList items={uploadLinkSteps} />
      </GuideSection>

      <GuideSection id="guest-testing" title="Guest Upload Testing">
        <StepList items={testSteps} />
      </GuideSection>

      <GuideSection id="reviewing-media" title="Reviewing Media">
        <p>Admin media statuses include Pending Review, Approved, Featured, Hidden / Private, and Not Approved.</p>
        <p>Use Admin Media to support all clients globally. Client users remain scoped to their own vaults.</p>
      </GuideSection>

      <GuideSection id="approval-workflow" title="Approval Workflow">
        <StepList items={approveSteps} />
      </GuideSection>

      <GuideSection id="hidden-workflow" title="Hidden / Private Media">
        <p>Use Hidden / Private when media should remain saved but not public.</p>
        <p>Hidden / Private media should not appear in the public gallery.</p>
      </GuideSection>

      <GuideSection id="not-approved-workflow" title="Not Approved Media">
        <p>Use Not Approved for blurry, duplicate, unrelated, inappropriate, or unwanted uploads.</p>
        <p>Not Approved media should not appear in the public gallery.</p>
      </GuideSection>

      <GuideSection id="featured-workflow" title="Featured Media">
        <p>Feature a photo or video when it is a special highlight or favorite memory.</p>
        <p>Featured media appears in the curated Memory Media Vault view and should still be approved media.</p>
      </GuideSection>

      <GuideSection id="public-gallery" title="Public Gallery">
        <p>The public gallery only shows approved/visible media. Pending and rejected media should not appear.</p>
        <p>Gallery URL format: /gallery/client-slug/event-slug.</p>
      </GuideSection>

      <GuideSection id="client-dashboard" title="Client Dashboard">
        <p>Clients can log in to see only their own assigned vaults, uploads, review pages, gallery links, storage guidance, settings, and instructions.</p>
        <p>Use the client dashboard to confirm the client experience after setup.</p>
      </GuideSection>

      <GuideSection id="multiple-vaults" title="Multiple Vaults Per Client">
        <p>One client can have multiple event vaults. Do not rebuild the app for each client or event.</p>
        <p>Create a new event vault under the existing client. Each vault gets its own guest upload link and gallery.</p>
      </GuideSection>

      <GuideSection id="practice-workflow" title="Practice Workflow">
        <Checklist items={practiceChecklist} />
        <GuideCallout title="Important">
          <p>Practice media should not be sent to real guests unless the vault is ready for live use.</p>
        </GuideCallout>
      </GuideSection>

      <GuideSection id="production-safety" title="Production Safety Rules">
        <Checklist items={safetyChecklist} />
      </GuideSection>

      <GuideSection id="storage-notes" title="Storage & Billing Admin Notes">
        <p>Review storage and billing reminders in Storage Setup and client-facing Storage & Billing.</p>
        <p>Explain that Cloudflare billing is managed through the client-owned Cloudflare account when applicable.</p>
        <p>Kreatethedate provides setup, design, connection, and support while services are active.</p>
        <p>Storage depends on media size and quantity. Large videos or large upload batches may require additional storage planning or fees.</p>
      </GuideSection>

      <GuideSection id="troubleshooting" title="Troubleshooting for Admin">
        <div className="grid gap-3">
          {troubleshooting.map((item) => (
            <GuideCallout key={item.issue} title={item.issue}>
              <p>{item.fix}</p>
            </GuideCallout>
          ))}
        </div>
      </GuideSection>

      <GuideSection id="quick-reference" title="Quick Reference Checklist">
        <GuideCallout title="Main admin steps" tone="dark">
          <p>Create Client - Create Event Vault - Generate Upload Link - Review Uploads - Approve - Check Gallery</p>
        </GuideCallout>
        <QuickReference items={adminQuickReference} />
        <GuideCallout title="Admin cleanup areas">
          <p>Admins can manage {adminAreas.join(", ")} from the protected admin portal.</p>
        </GuideCallout>
        <Link
          to="/admin/events"
          className="guide-actions focus-ring inline-flex rounded-full bg-ktd-black px-5 py-3 text-sm font-extrabold text-ktd-softGold"
        >
          Start with Events
        </Link>
      </GuideSection>

      <GuideSection id="glossary" title="Glossary of Terms">
        <Glossary terms={glossary} />
        <GuideCallout title="Future-ready note">
          <p>
            A future Admin Command Center may connect Admin Forms, Memory Media Vault Admin, partner/referral admin,
            client/project admin, payments or invoices, settings/access, user manuals, and support notes.
          </p>
          <p>
            Keep setup scalable: one reusable Memory Media Vault platform, multiple clients, multiple event vaults per client,
            no app rebuild per client, and room for future white-label or ClientFlow Studio expansion.
          </p>
        </GuideCallout>
      </GuideSection>
    </div>
  );
}
